import {
  BoxProps,
  ColorProps,
  Heading,
  HeadingProps,
  Text,
  TextProps,
} from '@chakra-ui/react'
import * as R from 'ramda'
import React from 'react'

export interface TypographyProps {
  alignRight?: boolean
  bold?: boolean
  capitalize?: boolean
  center?: boolean
  color?: ColorProps['color']
  lowercase?: boolean
  overline?: boolean
  strike?: boolean
  underline?: boolean
  uppercase?: boolean
  whitespace?: 'pre' | 'pre-wrap' | 'pre-line' | 'nowrap' | 'break-spaces'
  wrapText?: boolean
}

const typographyPropNames = [
  'alignRight',
  'bold',
  'capitalize',
  'center',
  'color',
  'lowercase',
  'overline',
  'strike',
  'underline',
  'uppercase',
  'whitespace',
  'wrapText',
]

const getTypographyStyles = (props: TypographyProps): Partial<BoxProps> =>
  R.reject(R.isNil)({
    fontWeight: props.bold ? 'bold' : undefined,
    textAlign: props.center ? 'center' : props.alignRight ? 'right' : 'left',
    textDecoration: props.underline
      ? 'underline'
      : props.overline
      ? 'overline'
      : props.strike
      ? 'line-through'
      : undefined,
    textTransform: props.uppercase
      ? 'uppercase'
      : props.capitalize
      ? 'capitalize'
      : props.lowercase
      ? 'lowercase'
      : undefined,
    whiteSpace: props.wrapText ? 'pre-line' : props.whitespace,
  })

const getDefaultHeaderStyles = (): Partial<BoxProps> =>
  R.reject(R.isNil)({
    fontFamily: 'body',
    fontWeight: 'medium',
    margin: 0,
  })

/**
 * Headers
 */

export const H1: React.FC<HeadingProps & TypographyProps> = ({
  color = 'deepPurple',
  ...props
}) => (
  <Heading
    as="h1"
    {...getDefaultHeaderStyles()}
    {...getTypographyStyles(props)}
    color={color}
    fontSize="h1"
    lineHeight="h1"
    marginBottom="28px"
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-h1"
  />
)

export const H2: React.FC<HeadingProps & TypographyProps> = ({
  color = 'deepPurple',
  ...props
}) => (
  <Heading
    as="h2"
    fontWeight="light"
    {...getDefaultHeaderStyles()}
    {...getTypographyStyles(props)}
    color={color}
    fontSize="h2"
    lineHeight="h2"
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-h2"
  />
)

export const H3: React.FC<HeadingProps & TypographyProps> = ({
  color = 'deepPurple',
  ...props
}) => (
  <Heading
    as="h3"
    {...getDefaultHeaderStyles()}
    {...getTypographyStyles(props)}
    color={color}
    fontSize="h3"
    lineHeight="h3"
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-h3"
  />
)

export const H4: React.FC<HeadingProps & TypographyProps> = ({
  color = 'deepPurple',
  ...props
}) => (
  <Heading
    as="h4"
    {...getDefaultHeaderStyles()}
    {...getTypographyStyles(props)}
    color={color}
    fontSize="h4"
    lineHeight="h4"
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-h4"
  />
)

export const H5: React.FC<HeadingProps & TypographyProps> = ({
  color = 'deepPurple',
  ...props
}) => (
  <Heading
    as="h5"
    {...getDefaultHeaderStyles()}
    {...getTypographyStyles(props)}
    color={color}
    fontSize="h5"
    lineHeight="h5"
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-h5"
  />
)

export const H6: React.FC<HeadingProps & TypographyProps> = ({
  color = 'deepPurple',
  ...props
}) => (
  <Heading
    as="h6"
    {...getDefaultHeaderStyles()}
    {...getTypographyStyles(props)}
    color={color}
    fontSize="h6"
    lineHeight="h6"
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-h6"
  />
)

/**
 * Various text styles
 */

export const CaptionText: React.FC<TextProps & TypographyProps> = ({
  color = 'neutral.90',
  ...props
}) => (
  <Text
    as="p"
    fontWeight="bold"
    {...getTypographyStyles(props)}
    color={color}
    fontSize="caption"
    lineHeight="caption"
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-caption"
  />
)

export const LeadText: React.FC<TextProps & TypographyProps> = ({
  color = 'neutral.90',
  ...props
}) => (
  <Text
    as="p"
    fontWeight="medium"
    {...getTypographyStyles(props)}
    color={color}
    fontSize="lead"
    lineHeight="lead"
    margin={0}
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-lead"
  />
)

export const LinkText: React.FC<TextProps & TypographyProps> = ({
  color = 'deepPurple',
  ...props
}) => (
  <Text
    as="span"
    fontWeight="bold"
    {...getTypographyStyles(props)}
    color={color}
    fontSize="body"
    lineHeight="body"
    margin={0}
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-link"
  />
)

export const StandardText: React.FC<TextProps & TypographyProps> = ({
  color = 'neutral.90',
  ...props
}) => (
  <Text
    as="p"
    className="typography-text"
    fontWeight="light"
    {...getTypographyStyles(props)}
    color={color}
    fontSize="body"
    lineHeight="body"
    margin={0}
    wordbreak="break-word"
    {...R.omit<object, string>(typographyPropNames, props)}
  />
)

export const TinyText: React.FC<TextProps & TypographyProps> = ({
  color = 'neutral.90',
  ...props
}) => (
  <Text
    as="p"
    fontWeight="medium"
    {...getTypographyStyles(props)}
    color={color}
    fontSize="tiny"
    lineHeight="tiny"
    margin={0}
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-tiny"
  />
)

export const NoWrap: React.FC<TextProps & TypographyProps> = ({ ...props }) => (
  <Text
    as="span"
    {...getTypographyStyles(props)}
    margin={0}
    {...R.omit<object, string>(typographyPropNames, props)}
    className="typography-nowrap"
    whiteSpace="nowrap"
  />
)
